import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, FileSearchIcon, HammerIcon, RocketIcon, SparklesIcon, LayoutGridIcon, WebhookIcon, HeadphonesIcon, RefreshCwIcon, FactoryIcon, PackageIcon, WrenchIcon, ChevronDownIcon } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
function CheckItem({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="flex items-start gap-3" data-id="element-658" data-mp="nvk8a8izz8lw">
      <div className="w-5 h-5 rounded-full bg-violet-electric/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-659" data-mp="nusm1obain8a">
        <CheckIcon className="w-3 h-3 text-violet-electric" data-id="element-660" data-mp="ssu01utuzwcw" />
      </div>
      <span className="text-white/70 text-sm leading-relaxed" data-id="element-661" data-mp="1gjfzwytpmabt">{children}</span>
    </div>;
}
export function PricingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <>
      {/* ═══════════════════════════════════════════════════════════════
                  SECTION 1: HERO
              ═══════════════════════════════════════════════════════════════ */}
      <section id="hero" className="relative w-full pt-32 pb-24 px-6 overflow-hidden bg-navy" data-id="element-662" data-mp="1sq0p9yx6unjn">

        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-663" data-mp="1nysfh1ry505y">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full animate-glow-pulse" style={{
          background: 'radial-gradient(ellipse at center, rgba(108, 92, 231, 0.2) 0%, rgba(200, 80, 192, 0.08) 40%, transparent 70%)'
        }} data-id="element-664" data-mp="21q71lhh5hhh8" />

        </div>

        <div className="relative z-10 max-w-3xl mx-auto text-center" data-id="element-665" data-mp="26pjvunacpqtu">
          <motion.span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} data-id="element-666" data-mp="26ngytjas6xfm">

            Pricing
          </motion.span>

          <motion.h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.08] tracking-tight mb-6" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} data-id="element-667" data-mp="160httafvn0rg">

            Start where it matters most. <br className="hidden sm:block" data-id="element-668" data-mp="1kwfw7age91c4" />
            <span className="bg-gradient-to-r from-violet-electric via-violet-vivid to-magenta-hot bg-clip-text text-transparent" data-id="element-669" data-mp="1ozlwm1gtryao">
              Expand when you're ready.
            </span>
          </motion.h1>

          <motion.p className="text-lg text-white/55 max-w-2xl mx-auto mb-10 leading-relaxed" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.25
        }} data-id="element-670" data-mp="1yovrfp31zv2h">

            Every Thruline engagement starts with a free discovery phase and a
            fixed-price project. Your team gets full platform access from day
            one — and when you're ready to expand, the next project works the
            same way.
          </motion.p>

          <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.35
        }} data-id="element-671" data-mp="3e8wglaek8qk5">

            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-672" data-mp="auil4nmepllh">

              Start Free Discovery
              <ArrowRightIcon className="w-4 h-4" data-id="element-673" data-mp="yjlbcjv3wmrl" />
            </a>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-navy-light to-transparent pointer-events-none" data-id="element-674" data-mp="tp79bylxvhdy" />
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                  SECTION 2: HOW IT WORKS — THREE STEPS
              ═══════════════════════════════════════════════════════════════ */}
      <section id="how-it-works" className="relative w-full bg-navy-light py-24 px-6" data-id="element-675" data-mp="9apehlfpjra9">

        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-violet-electric/20 to-transparent" data-id="element-676" data-mp="x0jq5zoaj9sx" />

        <div className="max-w-5xl mx-auto" data-id="element-677" data-mp="n9p0elbu5s2q">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={fadeUp} transition={{
          duration: 0.5
        }} data-id="element-678" data-mp="n8nko3jvkheo">

            <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-679" data-mp="1fw9qligkry4p">
              How it works
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" data-id="element-680" data-mp="35egdd96uvdlf">
              Every project follows three steps.
            </h2>
            <p className="text-white/45 text-base max-w-2xl mx-auto" data-id="element-681" data-mp="30ekhxyc84uhh">
              Whether it's your first module or your fourth expansion, the
              process is the same: we scope it, we build it, you run it.
            </p>
          </motion.div>

          {/* Phase Cards */}
          <motion.div className="grid grid-cols-1 lg:grid-cols-3 gap-6" variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} data-id="element-682" data-mp="2miqc5o6qyl8l">

            {/* Step 1: Discovery */}
            <motion.div variants={fadeUp} transition={{
            duration: 0.5
          }} className="relative rounded-2xl border border-white/[0.06] bg-white/[0.03] backdrop-blur-sm overflow-hidden" data-id="element-683" data-mp="2rf76x18gbxun">

              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-emerald-500" data-id="element-684" data-mp="6q9f3ana51gm" />
              <div className="p-8" data-id="element-685" data-mp="2zj6pcp9rp9cl">
                <div className="flex items-center gap-3 mb-6" data-id="element-686" data-mp="2zmccw02r0367">
                  <div className="w-10 h-10 rounded-xl bg-emerald-400/15 flex items-center justify-center" data-id="element-687" data-mp="34hrn8i1jrgxv">
                    <FileSearchIcon className="w-5 h-5 text-emerald-400" data-id="element-688" data-mp="39inriwpt953t" />
                  </div>
                  <div data-id="element-689" data-mp="3dj7qm5ty2isl">
                    <p className="text-white/40 text-[10px] font-bold uppercase tracking-widest" data-id="element-690" data-mp="3dhesxg1b78wq">
                      Step 1
                    </p>
                    <h3 className="text-lg font-semibold text-white" data-id="element-691" data-mp="2ltmpbr7kmq6z">
                      Discovery
                    </h3>
                  </div>
                </div>

                <div className="inline-flex items-baseline gap-1 mb-6" data-id="element-692" data-mp="2ukn30nr9hvjp">
                  <span className="text-3xl font-bold text-emerald-400" data-id="element-693" data-mp="2umq0ttynosyz">
                    Free
                  </span>
                </div>

                <p className="text-white/50 text-sm leading-relaxed mb-6" data-id="element-694" data-mp="223ss2xm37ivd">
                  You share your operational documents — SOPs, spreadsheets,
                  emails, whatever captures how your team actually works.
                  Thruline's system ingests them and reconstructs your
                  workflows. A consultant reviews the output, identifies gaps,
                  and builds a scoped project plan.
                </p>

                <div className="space-y-3" data-id="element-695" data-mp="1gneg5ed2t3ft">
                  <h4 className="text-white/30 text-[10px] font-bold uppercase tracking-widest" data-id="element-696" data-mp="1bmgzj67pshgf">
                    What you walk away with
                  </h4>
                  <CheckItem data-id="element-697" data-mp="16tnyasbgfr93">
                    A requirements map built from your own documents
                  </CheckItem>
                  <CheckItem data-id="element-698" data-mp="xvc4yij7efcy">
                    Consultant's assessment: what works, what's broken, what the
                    system should do
                  </CheckItem>
                  <CheckItem data-id="element-699" data-mp="nwmmanjhgqvn">
                    A scoped project plan with a fixed-price quote
                  </CheckItem>
                  <CheckItem data-id="element-700" data-mp="2bt2ojhr927iy">
                    No cost. No obligation. Walk away if it's not right.
                  </CheckItem>
                </div>
              </div>
            </motion.div>

            {/* Step 2: Implementation */}
            <motion.div variants={fadeUp} transition={{
            duration: 0.5
          }} className="relative rounded-2xl border border-violet-electric/20 bg-white/[0.05] backdrop-blur-sm overflow-hidden ring-1 ring-violet-electric/10" data-id="element-701" data-mp="22scwmp5411ai">

              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-electric to-violet-vivid" data-id="element-702" data-mp="1xu2zi8ztrqm9" />
              <div className="p-8" data-id="element-703" data-mp="1k366ih1zl7eo">
                <div className="flex items-center gap-3 mb-6" data-id="element-704" data-mp="1k01wv45r5jgm">
                  <div className="w-10 h-10 rounded-xl bg-violet-electric/15 flex items-center justify-center" data-id="element-705" data-mp="1o4rrkpoocrzy">
                    <HammerIcon className="w-5 h-5 text-violet-electric" data-id="element-706" data-mp="1a4h13nra1ffb" />
                  </div>
                  <div data-id="element-707" data-mp="1f2ij05cyv8p7">
                    <p className="text-white/40 text-[10px] font-bold uppercase tracking-widest" data-id="element-708" data-mp="1f54nfw4k9837">
                      Step 2
                    </p>
                    <h3 className="text-lg font-semibold text-white" data-id="element-709" data-mp="qiykzkmfyv4j">
                      Implementation
                    </h3>
                  </div>
                </div>

                <div className="inline-flex items-baseline gap-1 mb-6" data-id="element-710" data-mp="10gmiujfcgiqx">
                  <span className="text-3xl font-bold text-white" data-id="element-711" data-mp="10ipjryi1bwaf">
                    Fixed price
                  </span>
                </div>

                <p className="text-white/50 text-sm leading-relaxed mb-6" data-id="element-712" data-mp="19ht2h1dai9oe">
                  You approve the project plan, and Thruline builds your system.
                  Every configuration, workflow, and integration is traced back
                  to a specific requirement from your documents. A consultant
                  verifies the output. Documentation and training materials are
                  generated automatically.
                </p>

                <div className="space-y-3" data-id="element-713" data-mp="1t37azm3n8yvj">
                  <h4 className="text-white/30 text-[10px] font-bold uppercase tracking-widest" data-id="element-714" data-mp="171et6oz18tpz">
                    What you walk away with
                  </h4>
                  <CheckItem data-id="element-715" data-mp="xajweqo5ubqa">
                    A fully configured project — the module(s) you scoped, ready
                    to operate
                  </CheckItem>
                  <CheckItem data-id="element-716" data-mp="s9e06ivak6c0">
                    Every configuration traced to a documented requirement
                  </CheckItem>
                  <CheckItem data-id="element-717" data-mp="1l0e8h60nnw9h">
                    Auto-generated documentation & training materials
                  </CheckItem>
                  <CheckItem data-id="element-718" data-mp="1g00akgsrq2p6">
                    Consultant-verified quality, not just automated output
                  </CheckItem>
                  <CheckItem data-id="element-719" data-mp="1c1j13qlvkg4l">
                    Data migration from your existing systems
                  </CheckItem>
                </div>
              </div>
            </motion.div>

            {/* Step 3: Operate */}
            <motion.div variants={fadeUp} transition={{
            duration: 0.5
          }} className="relative rounded-2xl border border-white/[0.06] bg-white/[0.03] backdrop-blur-sm overflow-hidden" data-id="element-720" data-mp="2c7mukfnadc6l">

              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-magenta-hot to-pink-400" data-id="element-721" data-mp="2l8kw8ra957m0" />
              <div className="p-8" data-id="element-722" data-mp="2pzkqtpvwfdlc">
                <div className="flex items-center gap-3 mb-6" data-id="element-723" data-mp="2q0m50387og7a">
                  <div className="w-10 h-10 rounded-xl bg-magenta-hot/15 flex items-center justify-center" data-id="element-724" data-mp="2uyxgsji2h0s5">
                    <RocketIcon className="w-5 h-5 text-magenta-hot" data-id="element-725" data-mp="2zw60wpmrzew1" />
                  </div>
                  <div data-id="element-726" data-mp="33ykjd8mj8cms">
                    <p className="text-white/40 text-[10px] font-bold uppercase tracking-widest" data-id="element-727" data-mp="33zc4fpii6ryt">
                      Step 3
                    </p>
                    <h3 className="text-lg font-semibold text-white" data-id="element-728" data-mp="19l1f7fxzxvsi">
                      Operate
                    </h3>
                  </div>
                </div>

                <div className="inline-flex items-baseline gap-2 mb-6" data-id="element-729" data-mp="1elywzrf3gnth">
                  <span className="text-3xl font-bold text-white" data-id="element-730" data-mp="2hvbomw04sr65">
                    Per user
                  </span>
                  <span className="text-white/40 text-sm" data-id="element-731" data-mp="2cxrzv25t4vzy">/month</span>
                </div>

                <p className="text-white/50 text-sm leading-relaxed mb-6" data-id="element-732" data-mp="2qwroi9tblaw4">
                  Your project goes live. Every licensed user gets access to the
                  full Thruline platform — not just the module you implemented,
                  but everything. Reports, dashboards, workflows, document
                  templates, all modules. No feature gating, no module-by-module
                  upsells.
                </p>

                <div className="space-y-3" data-id="element-733" data-mp="35kmj5vqw1pbu">
                  <h4 className="text-white/30 text-[10px] font-bold uppercase tracking-widest" data-id="element-734" data-mp="35kw9xljrc0il">
                    What you walk away with
                  </h4>
                  <CheckItem data-id="element-735" data-mp="3elkkg69xziqq">
                    Full platform access for every licensed user
                  </CheckItem>
                  <CheckItem data-id="element-736" data-mp="22rnb6ao78kr">
                    All modules included, not just the one you implemented first
                  </CheckItem>
                  <CheckItem data-id="element-737" data-mp="6y6zltt14wf2">
                    Unlimited workflows, reports, dashboards & document
                    templates
                  </CheckItem>
                  <CheckItem data-id="element-738" data-mp="3t6gg10kytpst">
                    Ongoing support from the team that built your system
                  </CheckItem>
                  <CheckItem data-id="element-739" data-mp="3feiaezqgqc1e">
                    A self-documenting ERP that trains your team as they use it
                  </CheckItem>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Repeatable cycle indicator */}
          <motion.div className="flex items-center justify-center mt-10 gap-3" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} transition={{
          duration: 0.5,
          delay: 0.2
        }} data-id="element-740" data-mp="3ahrss7z0f8dv">

            <RefreshCwIcon className="w-4 h-4 text-violet-electric/50" data-id="element-741" data-mp="2tgfb79dfi04c" />
            <p className="text-white/30 text-sm" data-id="element-742" data-mp="2thqdt5v511tv">
              Same process for every expansion. Discovery is always free.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                  SECTION 3: WHEN YOU'RE READY TO GROW
              ═══════════════════════════════════════════════════════════════ */}
      <section id="expansion" className="relative w-full bg-navy py-24 px-6" data-id="element-743" data-mp="2an1qdx3fja2g">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" data-id="element-744" data-mp="25p083edtves1" />

        <div className="max-w-5xl mx-auto" data-id="element-745" data-mp="1vsnbo8qwemtl">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={fadeUp} transition={{
          duration: 0.5
        }} data-id="element-746" data-mp="1vtyfu9o99wl8">

            <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-747" data-mp="2hrc36p8v268">
              Expand on your terms
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" data-id="element-748" data-mp="6hjq3yxeq0iz">
              Your next project works the same way.
            </h2>
            <p className="text-white/45 text-base max-w-2xl mx-auto" data-id="element-749" data-mp="g9fnt8vkzomo">
              You don't have to buy your entire ERP on day one. Start with the
              area that hurts the most, get it running, then expand when the
              business is ready.
            </p>
          </motion.div>

          {/* Narrative blocks */}
          <motion.div className="max-w-3xl mx-auto space-y-6 mb-16" variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} data-id="element-750" data-mp="3onelcey7bi2a">

            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} data-id="element-751" data-mp="13y8mhd3l8akz">

              <h3 className="text-white font-semibold text-base mb-2" data-id="element-752" data-mp="18wa742gnwhyk">
                Start with what matters most.
              </h3>
              <p className="text-white/45 text-sm leading-relaxed" data-id="element-753" data-mp="eamqg0qqzqnc">
                Most companies don't need their entire ERP on day one. You start
                with the area that hurts the most — maybe it's production, maybe
                it's inventory, maybe it's purchasing. You get that running.
                Your team learns the system in a real operational context. Then,
                when the next pain point surfaces or the business grows into a
                new area, you scope another project.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} data-id="element-754" data-mp="aakrhzcedbbr">

              <h3 className="text-white font-semibold text-base mb-2" data-id="element-755" data-mp="5gq4on1hwg8g">
                Each expansion follows the same three steps.
              </h3>
              <p className="text-white/45 text-sm leading-relaxed" data-id="element-756" data-mp="38zji8zu9x52m">
                Free discovery, fixed-price project, same license. Your
                documents go in, a scoped plan comes out, you approve the price
                before work begins. Because your first project is already live,
                the system has context — the new project builds on top of what's
                already there, with the same traceability, the same
                documentation, and the same support team.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} data-id="element-757" data-mp="2789w3pqc90hu">

              <h3 className="text-white font-semibold text-base mb-2" data-id="element-758" data-mp="2c5s8jq3kdxul">
                You're never locked in, and you're never locked out.
              </h3>
              <p className="text-white/45 text-sm leading-relaxed" data-id="element-759" data-mp="227m7tbthuxrb">
                There's no penalty for starting small and no gate preventing you
                from expanding. Your license already includes every module. The
                only cost for an expansion is the project itself — scoped to
                exactly what you need, priced before you commit.
              </p>
            </motion.div>
          </motion.div>

          {/* Example expansion journey */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={fadeUp} transition={{
          duration: 0.5,
          delay: 0.1
        }} data-id="element-760" data-mp="19ftyp2uv81os">

            <h3 className="text-white/30 text-[10px] font-bold uppercase tracking-widest text-center mb-8" data-id="element-761" data-mp="217jc2mpajzlr">
              Example expansion journey
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-762" data-mp="1seziw1rrqn8b">
              {/* Project 1 */}
              <div className="relative rounded-2xl border border-white/[0.06] bg-white/[0.03] p-6 overflow-hidden" data-id="element-763" data-mp="1nfwd2f3yscza">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-electric to-violet-vivid" data-id="element-764" data-mp="2kvsfxu075u13" />
                <div className="flex items-center gap-3 mb-4" data-id="element-765" data-mp="2g3ipmcv66rya">
                  <div className="w-8 h-8 rounded-lg bg-violet-electric/15 flex items-center justify-center" data-id="element-766" data-mp="2nfygdrplr2cw">
                    <FactoryIcon className="w-4 h-4 text-violet-electric" data-id="element-767" data-mp="2se9sk93ddoya" />
                  </div>
                  <div data-id="element-768" data-mp="2xdcwtrbizr57">
                    <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest" data-id="element-769" data-mp="2xd3621inpfyg">
                      Project 1
                    </p>
                    <p className="text-white text-sm font-semibold" data-id="element-770" data-mp="36ekin9wuuzie">Day one</p>
                  </div>
                </div>
                <h4 className="text-white font-semibold text-base mb-2" data-id="element-771" data-mp="3bcvt9muz5e1o">
                  Production + BoMs
                </h4>
                <p className="text-white/40 text-xs mb-4" data-id="element-772" data-mp="3g4w07z9gp77r">
                  Production tracking is manual and error-prone
                </p>
                <div className="space-y-2 text-xs" data-id="element-773" data-mp="3p52bdehotv3r">
                  <div className="flex justify-between" data-id="element-774" data-mp="3p6dfjff1p4ve">
                    <span className="text-white/30" data-id="element-775" data-mp="3u3vlcwxrn3z6">Discovery</span>
                    <span className="text-emerald-400 font-medium" data-id="element-776" data-mp="3u22htqig8zve">Free</span>
                  </div>
                  <div className="flex justify-between" data-id="element-777" data-mp="yxfwomdzubhv">
                    <span className="text-white/30" data-id="element-778" data-mp="13vhktlqa1107">Implementation</span>
                    <span className="text-white/60 font-medium" data-id="element-779" data-mp="17vjm3ts3nsey">
                      Fixed-price project
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-780" data-mp="1ct1kiq8hqp8p">
                    <span className="text-white/30" data-id="element-781" data-mp="1hv0lxuko7c30">License</span>
                    <span className="text-white/60 font-medium" data-id="element-782" data-mp="1mtbyid2co0ox">
                      Per user / month
                    </span>
                  </div>
                </div>
              </div>

              {/* Project 2 */}
              <div className="relative rounded-2xl border border-white/[0.06] bg-white/[0.03] p-6 overflow-hidden" data-id="element-783" data-mp="1vlvrawvynb1u">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-vivid to-magenta-hot" data-id="element-784" data-mp="20m1lajppd9n3" />
                <div className="flex items-center gap-3 mb-4" data-id="element-785" data-mp="25k3556uydczm">
                  <div className="w-8 h-8 rounded-lg bg-magenta-hot/15 flex items-center justify-center" data-id="element-786" data-mp="2fo0ah4l437zf">
                    <PackageIcon className="w-4 h-4 text-magenta-hot" data-id="element-787" data-mp="2anm19j7rxrzt" />
                  </div>
                  <div data-id="element-788" data-mp="25qlvl9eu6r98">
                    <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest" data-id="element-789" data-mp="25q2mmi75zd7c">
                      Project 2
                    </p>
                    <p className="text-white text-sm font-semibold" data-id="element-790" data-mp="2yeria1pgaqbp">
                      6 months later
                    </p>
                  </div>
                </div>
                <h4 className="text-white font-semibold text-base mb-2" data-id="element-791" data-mp="2thhlg0zqfcdu">
                  Inventory + Purchasing
                </h4>
                <p className="text-white/40 text-xs mb-4" data-id="element-792" data-mp="2knmq1koex0be">
                  Stockouts and over-ordering are costing money
                </p>
                <div className="space-y-2 text-xs" data-id="element-793" data-mp="3h60syeayjl4v">
                  <div className="flex justify-between" data-id="element-794" data-mp="3cdr5r6188z6a">
                    <span className="text-white/30" data-id="element-795" data-mp="3cc6b7gewxg8f">Discovery</span>
                    <span className="text-emerald-400 font-medium" data-id="element-796" data-mp="1ahs4t1pts3d0">Free</span>
                  </div>
                  <div className="flex justify-between" data-id="element-797" data-mp="1ff0lsyz8m1co">
                    <span className="text-white/30" data-id="element-798" data-mp="10r76lfe2d0um">Implementation</span>
                    <span className="text-white/60 font-medium" data-id="element-799" data-mp="10otbx7napqnl">
                      Fixed-price project
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-800" data-mp="rp6bao2e4ovl">
                    <span className="text-white/30" data-id="element-801" data-mp="woso4web433v">License</span>
                    <span className="text-white/60 font-medium" data-id="element-802" data-mp="wqdfkd5br5xi">
                      Per user / month (unchanged)
                    </span>
                  </div>
                </div>
              </div>

              {/* Project 3 */}
              <div className="relative rounded-2xl border border-white/[0.06] bg-white/[0.03] p-6 overflow-hidden" data-id="element-803" data-mp="2c62mf4og58cr">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-magenta-hot to-pink-400" data-id="element-804" data-mp="2h55qomwlrajo" />
                <div className="flex items-center gap-3 mb-4" data-id="element-805" data-mp="5iby0yp6qmxk">
                  <div className="w-8 h-8 rounded-lg bg-pink-400/15 flex items-center justify-center" data-id="element-806" data-mp="iqzuqhuailw">
                    <WrenchIcon className="w-4 h-4 text-pink-400" data-id="element-807" data-mp="f7vj8b0desvl" />
                  </div>
                  <div data-id="element-808" data-mp="agwymz8cn8mj">
                    <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest" data-id="element-809" data-mp="aflwt4ygsay2">
                      Project 3
                    </p>
                    <p className="text-white text-sm font-semibold" data-id="element-810" data-mp="o9wgh7vltex9">Year two</p>
                  </div>
                </div>
                <h4 className="text-white font-semibold text-base mb-2" data-id="element-811" data-mp="y6rbvhwkyz7b">
                  Field Operations
                </h4>
                <p className="text-white/40 text-xs mb-4" data-id="element-812" data-mp="t8g2176acop3">
                  New service division launched
                </p>
                <div className="space-y-2 text-xs" data-id="element-813" data-mp="2zxukk1mwu3p8">
                  <div className="flex justify-between" data-id="element-814" data-mp="2uzb0y7vsh401">
                    <span className="text-white/30" data-id="element-815" data-mp="2v0ugtxdj9d1a">Discovery</span>
                    <span className="text-emerald-400 font-medium" data-id="element-816" data-mp="2wf5a9hijfioy">Free</span>
                  </div>
                  <div className="flex justify-between" data-id="element-817" data-mp="31d6rry0bf9yb">
                    <span className="text-white/30" data-id="element-818" data-mp="36dbbdwwisksr">Implementation</span>
                    <span className="text-white/60 font-medium" data-id="element-819" data-mp="36c08s0et9j38">
                      Fixed-price project
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-820" data-mp="2dqi5l049wvbw">
                    <span className="text-white/30" data-id="element-821" data-mp="2io0hmzdl7qo4">License</span>
                    <span className="text-white/60 font-medium" data-id="element-822" data-mp="2iojsjw4tlesn">
                      New users added
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                  SECTION 4: WHAT EVERY LICENSE INCLUDES
              ═══════════════════════════════════════════════════════════════ */}
      <section id="platform-access" className="relative w-full bg-navy-light py-24 px-6" data-id="element-823" data-mp="h1czn6g6ssxw">

        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-violet-electric/20 to-transparent" data-id="element-824" data-mp="d246a2p26x2v" />

        <div className="max-w-5xl mx-auto" data-id="element-825" data-mp="32vengho1ujo">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={fadeUp} transition={{
          duration: 0.5
        }} data-id="element-826" data-mp="31tsogz43nn6">

            <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-827" data-mp="3ph6bh7zkpg8m">
              Full platform access
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" data-id="element-828" data-mp="3giuemo845m7q">
              One license. The full platform.
            </h2>
            <p className="text-white/45 text-base max-w-xl mx-auto" data-id="element-829" data-mp="36jlpc8o70vrp">
              Every user gets everything. No tiers. No upgrades. No modules to
              unlock.
            </p>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-8" variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} data-id="element-830" data-mp="31ut0yymvpdjf">

            {/* Platform */}
            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-831" data-mp="2rxy1acevd73k">

              <div className="flex items-center gap-3 mb-5" data-id="element-832" data-mp="3klapss4lbozi">
                <LayoutGridIcon className="w-5 h-5 text-violet-electric" data-id="element-833" data-mp="3kksovi6zgml9" />
                <h3 className="text-white font-semibold text-sm tracking-wide" data-id="element-834" data-mp="36te353loy7ai">
                  Platform
                </h3>
              </div>
              <div className="space-y-3" data-id="element-835" data-mp="2fz3j3bgxli3">
                <CheckItem data-id="element-836" data-mp="2ewc8ua8hh1n">
                  All Thruline modules (Production, Inventory, Purchasing, BoMs,
                  and more)
                </CheckItem>
                <CheckItem data-id="element-837" data-mp="1rwt9eyjdsrd7">
                  Unlimited triggered and scheduled workflows
                </CheckItem>
                <CheckItem data-id="element-838" data-mp="1myrsaj5in24d">Unlimited reports and dashboards</CheckItem>
                <CheckItem data-id="element-839" data-mp="21mwazn4kjdn5">Unlimited document templates</CheckItem>
                <CheckItem data-id="element-840" data-mp="21m4f0b727dwc">Full mobile access</CheckItem>
              </div>
            </motion.div>

            {/* With every project */}
            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-841" data-mp="18xytvnc3fmvz">

              <div className="flex items-center gap-3 mb-5" data-id="element-842" data-mp="1418gx8vl57es">
                <SparklesIcon className="w-5 h-5 text-violet-electric" data-id="element-843" data-mp="1ixfvnto85gmo" />
                <h3 className="text-white font-semibold text-sm tracking-wide" data-id="element-844" data-mp="1dws9pmeurpzb">
                  With every project
                </h3>
              </div>
              <div className="space-y-3" data-id="element-845" data-mp="2u9qlpsbl2ynf">
                <CheckItem data-id="element-846" data-mp="2pd02wx8e9oyb">
                  AI-powered document ingestion and requirements construction
                </CheckItem>
                <CheckItem data-id="element-847" data-mp="mr4locdqwb4t">Expert consultant review and gap analysis</CheckItem>
                <CheckItem data-id="element-848" data-mp="ro4ma7rtsloj">
                  Traced configuration (every setting linked to a requirement)
                </CheckItem>
                <CheckItem data-id="element-849" data-mp="wmodonp6upoa">
                  Auto-generated documentation and training materials
                </CheckItem>
                <CheckItem data-id="element-850" data-mp="16dsrvl71i6i2">Data migration</CheckItem>
              </div>
            </motion.div>

            {/* Integrations */}
            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-851" data-mp="1aemg6foe9nbh">

              <div className="flex items-center gap-3 mb-5" data-id="element-852" data-mp="1kdjyh0v88km1">
                <WebhookIcon className="w-5 h-5 text-violet-electric" data-id="element-853" data-mp="1kd1wropspe6q" />
                <h3 className="text-white font-semibold text-sm tracking-wide" data-id="element-854" data-mp="3h6vl377lex7o">
                  Integrations
                </h3>
              </div>
              <div className="space-y-3" data-id="element-855" data-mp="3m6rp36gaion8">
                <CheckItem data-id="element-856" data-mp="3elnr9oxb0fpc">
                  Pre-built integrations (QuickBooks, Shopify, Slack, Stripe,
                  etc.)
                </CheckItem>
                <CheckItem data-id="element-857" data-mp="39sumx25qz9ds">Custom inbound webhooks</CheckItem>
                <CheckItem data-id="element-858" data-mp="34wdye6umyt06">Single Sign-On (SSO)</CheckItem>
                <CheckItem data-id="element-859" data-mp="2zwrl5xet5crd">
                  Custom integrations (scoped during implementation)
                </CheckItem>
                <CheckItem data-id="element-860" data-mp="1duk688slnod">
                  GovCloud available for government contractors
                </CheckItem>
              </div>
            </motion.div>

            {/* Support */}
            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-861" data-mp="3nlaw6kp8s3n5">

              <div className="flex items-center gap-3 mb-5" data-id="element-862" data-mp="2c6x9hj685u8p">
                <HeadphonesIcon className="w-5 h-5 text-violet-electric" data-id="element-863" data-mp="2c88gdruyvi41" />
                <h3 className="text-white font-semibold text-sm tracking-wide" data-id="element-864" data-mp="276ss7qxhsvhf">
                  Support
                </h3>
              </div>
              <div className="space-y-3" data-id="element-865" data-mp="15dv4iyprv8hi">
                <CheckItem data-id="element-866" data-mp="10hwel6ospiez">
                  Ongoing support from the team that built your system
                </CheckItem>
                <CheckItem data-id="element-867" data-mp="1f7kcrz1ok0yh">Workflow development assistance</CheckItem>
                <CheckItem data-id="element-868" data-mp="1af0xmxncgxrk">
                  System evolution as your operations change
                </CheckItem>
                <CheckItem data-id="element-869" data-mp="1o5e6r3bz2iih">
                  Self-documenting ERP — always-current training materials
                </CheckItem>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                  SECTION 5: WHY THIS MODEL WORKS
              ═══════════════════════════════════════════════════════════════ */}
      <section id="philosophy" className="relative w-full bg-navy py-24 px-6" data-id="element-870" data-mp="3syhvixhjaf6">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" data-id="element-871" data-mp="3uyq6uezj0h0e" />

        <div className="max-w-3xl mx-auto" data-id="element-872" data-mp="2fdxvuak8525n">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={fadeUp} transition={{
          duration: 0.5
        }} data-id="element-873" data-mp="2fdo52krcuqyw">

            <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-874" data-mp="25m1n85kihtn6">
              Our philosophy
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-875" data-mp="1vqzvr32rkfhf">
              The logic behind the pricing.
            </h2>
          </motion.div>

          <motion.div className="space-y-4" variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} data-id="element-876" data-mp="1mp0ficzkpffj">

            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-877" data-mp="3h3jggb4gvavg">

              <h3 className="text-white font-semibold text-base mb-3" data-id="element-878" data-mp="3fnp9371a3696">
                "Why is discovery free?"
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-879" data-mp="35o85qw9i14gd">
                Because we're not interviewing your team for six weeks. Thruline
                reads your documents and reconstructs your workflows in hours,
                not months. The discovery phase produces a real, scoped project
                plan with a fixed-price quote. If it doesn't make sense for you,
                you've lost nothing. We can do this because the Thruline ERP
                Factory makes discovery dramatically cheaper than traditional
                consulting — so we absorb the cost.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-880" data-mp="kz0slve88p2z">

              <h3 className="text-white font-semibold text-base mb-3" data-id="element-881" data-mp="og6f83um5wrg">
                "Why fixed-price projects instead of hourly billing?"
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-882" data-mp="shrwe1e6kx7g">
                Because hourly billing is how ERP implementations balloon. The
                consultant's meter starts running in the first discovery meeting
                and doesn't stop until months after go-live. Thruline scopes
                each project during discovery and quotes a number you approve
                before work begins. The Thruline ERP Factory automates the
                production work and focuses consultant time on expert review.
                The result: predictable cost, no overruns, and you know exactly
                what you're paying for.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-883" data-mp="1q73c6smdaljz">

              <h3 className="text-white font-semibold text-base mb-3" data-id="element-884" data-mp="2ruoz1f1gptbs">
                "Why pay per project instead of one big upfront fee?"
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-885" data-mp="31la49lbn5w3o">
                Because most companies don't need their entire ERP on day one,
                and paying for everything upfront means paying for modules you
                won't use for months or years. The per-project model lets you
                start where the pain is, prove value with your team, and expand
                when the business is ready — not when a sales contract forces
                you to. Each project is a self-contained engagement with its own
                scope, its own price, and its own deliverable. You're never
                overpaying for capabilities you haven't activated.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-886" data-mp="3pa2rmte76vmo">

              <h3 className="text-white font-semibold text-base mb-3" data-id="element-887" data-mp="5o2j7i8v5md7">
                "Why one license tier instead of Starter, Pro, and Enterprise?"
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-888" data-mp="3myk4zn8rsfj2">
                Because feature gating is how legacy ERP vendors extract revenue
                after the sale. You buy the base tier, realize you need
                something locked behind the next one, and upgrade under
                pressure. Thruline includes everything in every license. The
                project determines which modules your team uses first; the
                license gives them access to all of them. When you're ready to
                activate a new module, you run a project — not an upgrade
                purchase.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                  SECTION 6: COMPARISON TABLE
              ═══════════════════════════════════════════════════════════════ */}
      <section id="comparison" className="relative w-full bg-navy-light py-24 px-6" data-id="element-889" data-mp="180lsttw1va05">

        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-violet-electric/20 to-transparent" data-id="element-890" data-mp="182gejaf5b65a" />

        <div className="max-w-4xl mx-auto" data-id="element-891" data-mp="y3hegg8g4itw">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={fadeUp} transition={{
          duration: 0.5
        }} data-id="element-892" data-mp="y4inyg9tcx9i">

            <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-893" data-mp="kbkq8n9wet2s">
              Comparison
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-894" data-mp="fb6kjbvrrv7x">
              How the old model compares.
            </h2>
          </motion.div>

          <motion.div className="rounded-2xl border border-white/[0.06] overflow-hidden" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={fadeUp} transition={{
          duration: 0.5,
          delay: 0.1
        }} data-id="element-895" data-mp="2lz5iazry33tk">

            {/* Header */}
            <div className="grid grid-cols-3 border-b border-white/[0.06]" data-id="element-896" data-mp="2zzg962t98ieq">
              <div className="p-5 bg-white/[0.01]" data-id="element-897" data-mp="23as221j2ufk3" />
              <div className="p-5 bg-white/[0.02] text-center border-l border-white/[0.06]" data-id="element-898" data-mp="237w7mn6fu90e">
                <span className="text-white/40 text-xs font-bold uppercase tracking-widest" data-id="element-899" data-mp="286fquftnd6p2">
                  Traditional ERP
                </span>
              </div>
              <div className="p-5 bg-violet-electric/[0.08] text-center border-l border-violet-electric/20" data-id="element-900" data-mp="2h2oq9uhmk5b7">
                <span className="text-violet-electric text-xs font-bold uppercase tracking-widest" data-id="element-901" data-mp="1kizb0cnkj4f0">
                  Thruline
                </span>
              </div>
            </div>

            {/* Rows */}
            {[['Discovery cost', 'Paid from meeting one', 'Free'], ['Pricing model', 'Hourly, open-ended', 'Fixed price per project, scoped upfront'], ['Scope clarity', 'Emerges over months', 'Defined before you commit'], ['Entry commitment', 'Buy the whole system upfront', 'Start with one project, expand later'], ['Feature access', 'Tiered / per-module licensing', 'Everything included in every license'], ['Timeline', '3–9 months', 'Weeks per project'], ['Documentation', 'Goes stale before go-live', 'Auto-generated, traced, always current'], ['Support model', 'Generic helpdesk', 'The team that built your system'], ['Traceability', 'None', 'Every config traced to a requirement'], ['Expansion cost', 'Module upgrade fees + re-implementation', 'New project, same process, fixed price']].map(([label, traditional, thruline], i) => <div key={label} className={`grid grid-cols-3 ${i < 9 ? 'border-b border-white/[0.04]' : ''}`} data-id="element-902" data-mp="1flisism341yj" data-mpi={i}>

                <div className="p-4 sm:p-5 bg-white/[0.01]" data-id="element-903" data-mp="1y91gpq1lrxyv">
                  <span className="text-white/80 text-sm font-medium" data-id="element-904" data-mp="1y7gmy2n44j22">
                    {label}
                  </span>
                </div>
                <div className="p-4 sm:p-5 bg-white/[0.02] border-l border-white/[0.06]" data-id="element-905" data-mp="1p9o1shupmh6r">
                  <span className="text-white/35 text-sm" data-id="element-906" data-mp="1u7g485szdlp7">{traditional}</span>
                </div>
                <div className="p-4 sm:p-5 bg-violet-electric/[0.04] border-l border-violet-electric/10" data-id="element-907" data-mp="1fijnzlgrt2di">
                  <span className="text-white/70 text-sm font-medium" data-id="element-908" data-mp="1ka1ok8evt0xy">
                    {thruline}
                  </span>
                </div>
              </div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                  SECTION 7: BOTTOM CTA
              ═══════════════════════════════════════════════════════════════ */}
      <section id="cta" className="relative w-full py-24 px-6 overflow-hidden" data-id="element-909" data-mp="11i8zgp2xnzvi">
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(135deg, #0D0B2E 0%, #1A1452 30%, #6C5CE7 70%, #C850C0 100%)'
      }} data-id="element-910" data-mp="1sk96oypq6cee" />

        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")"
      }} data-id="element-911" data-mp="1xlf2j5eomfs5" />


        <motion.div className="relative z-10 max-w-3xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={fadeUp} transition={{
        duration: 0.6
      }} data-id="element-912" data-mp="zwddi3zd72md">

          <span className="text-white/60 text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-913" data-mp="2uaedwtakbxok">
            Get started
          </span>
          <h2 className="text-3xl sm:text-5xl font-bold text-white tracking-tight mb-6" data-id="element-914" data-mp="nlcmippok8jb">
            Start with what matters most.
            <br className="hidden sm:block" data-id="element-915" data-mp="inkiwyfoaxza" />
            Discovery is free.
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-xl mx-auto" data-id="element-916" data-mp="xbntym8kozuy">
            Send us your documents. We'll send you a project plan and a
            fixed-price quote. No cost, no commitment.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-917" data-mp="16asozikxeb2d">
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-918" data-mp="11bz9xlpzqc03">

              Start Free Discovery
              <ArrowRightIcon className="w-4 h-4" data-id="element-919" data-mp="3i0v2f9swgr20" />
            </a>
            <a href="/resources/thesis" className="inline-flex items-center px-8 py-3.5 rounded-full border border-white/30 text-white font-medium text-sm hover:border-white/60 transition-all" data-id="element-920" data-mp="3d2jrswus6ciq">

              Read the ERP Factory Thesis
            </a>
          </div>
        </motion.div>
      </section>
    </>;
}